import React from "react";
import {
  useChannelStateContext,
  useChatContext,
  useTypingContext,
} from "stream-chat-react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Badge,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

// Optional: for human-readable "last seen" times
import { formatDistanceToNowStrict, parseISO } from "date-fns";

const CustomChannelHeader = ({ onBack }) => {
  const { channel, watcherCount } = useChannelStateContext();
  const { client } = useChatContext();
  const { typing } = useTypingContext();
  const theme = useTheme();
  const members = Object.values(channel?.state?.members || {});
  const otherMember = members.find((m) => m.user.id !== client.user.id);

  const user = otherMember?.user;
  const displayName = user?.name || "Unknown User";
  const displayImage = user?.image || "";

  const isOnline = !!user?.online;
  const lastActive = user?.last_active ? parseISO(user.last_active) : null;

  // Check if the other user is currently typing in this channel
  const isTyping = typing && typing[user?.id];

  const getLastSeenText = () => {
    if (isOnline) return "Online";
    if (lastActive)
      return `Last seen ${formatDistanceToNowStrict(lastActive, {
        addSuffix: true,
      })}`;
    return "Offline";
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      px={2}
      py={0.6}
      sx={{
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        bgcolor: "#1d1915"
      }}
    >
      {/* Left side: Back + Avatar + Info */}
      <Box display="flex" alignItems="center" gap={1.5}>
        {onBack && (
          <IconButton onClick={onBack} size="small">
            <ArrowBackIcon sx={{ color: "white" }} />
          </IconButton>
        )}

        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={
            <FiberManualRecordIcon
              sx={{
                fontSize: 12,
                color: isOnline ? "#4CAF50" : "gray",
              }}
            />
          }
        >
          <Avatar
            src={displayImage}
            alt={displayName}
            sx={{ width: 40, height: 40 }}
          />
        </Badge>

        <Box>
          <Typography
            variant="subtitle1"
            sx={{ color: "white", fontWeight: 500 }}
          >
            {displayName}
          </Typography>

          {isTyping ? (
            <Typography
              variant="caption"
              sx={{ color: "#4FC3F7", fontStyle: "italic" }}
            >
              typing…
            </Typography>
          ) : (
            <Typography
              variant="caption"
              sx={{
                color: isOnline ? "#81C784" : "rgba(255,255,255,0.6)",
              }}
            >
              {getLastSeenText()}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Right side: Channel stats or actions */}
      <Box textAlign="right">
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
          {watcherCount > 1 ? `${watcherCount} online now` : "Only you here"}
        </Typography>
      </Box>
    </Box>
  );
};

export default CustomChannelHeader;
